/** @format */

/* eslint @typescript-eslint/prefer-nullish-coalescing: "warn" -- Legacy code: do not use as an example. */
import isPropValid from '@emotion/is-prop-valid';
import { Heading } from '@mpb-com/feather/components/atoms/heading';
import { HeadingSize } from '@mpb-com/feather/components/atoms/heading/types';
import {
    Skeleton,
    SkeletonVariant,
} from '@mpb-com/feather/components/atoms/skeleton';
import { Thumbnail } from '@mpb-com/feather/components/atoms/thumbnail';
import { ThumbnailSkeleton } from '@mpb-com/feather/components/atoms/thumbnail/skeleton';
import { useTranslation } from 'next-i18next';
import React, {
    FunctionComponent,
    KeyboardEvent,
    MouseEvent,
    MutableRefObject,
    useEffect,
    useRef,
    useState,
} from 'react';
import styled from 'styled-components';

import { CloudflareImageSize } from '@toucan/client/global/cloudflare';
import {
    Product,
    fallbackImagePath,
    isConditionSparesAndRepairs,
} from '@toucan/client/global/product';

import { CardBackground } from '@toucan/client/shared/components/card-background';
import { FormattedCondition } from '@toucan/client/shared/components/formatted-condition';
import {
    BufferedMeasurements,
    BufferedMeasurementsStatus,
    useBufferedMeasurements,
} from '@toucan/client/shared/hooks/use-buffered-mesurements';
import {
    ElementBreakpointsQuery,
    ElementBreakpointsStatus,
    useElementBreakpoints,
} from '@toucan/client/shared/hooks/use-element-breakpoints';
import { useRecentlyViewedProducts } from '@toucan/client/shared/hooks/use-recently-viewed-products';

import { CloudflareImage } from '@toucan/client/blocks/components/cloudflare-image';
import { Attributes } from '@toucan/client/blocks/components/product/product-card-attributes';
import { Price } from '@toucan/client/blocks/components/product/product-card-price';
import { Sku } from '@toucan/client/blocks/components/product/product-card-sku';

import { FavouriteProductButton } from '@toucan/client/features/search/components/product-card/components/favourite-product-button';
import { ProductBox } from '@toucan/client/features/search/components/product-card/components/product-box';
import { ProductBoxSkeleton } from '@toucan/client/features/search/components/product-card/components/product-box/skeleton';
import UspTag from '@toucan/client/features/search/components/product-card/components/usp-tag';

import { AddButton } from './components/add-button';
import { AddToBasketButton } from './components/add-to-basket-button';
import { BoxListHeader } from './components/box-list-header';
import { getWidthDependentProductBoxProps } from './product-card.helpers';
import { styles } from './product-card.styles';
import { NUM_EXPECTED_ATTRIBUTES, getThumbnailHeight } from './style-helpers';

// eslint-disable-next-line @mpb-com/mpb/require-constants-file -- Legacy code: do not use as an example.
export enum ProductCardButtons {
    BASKET = 'add_to_basket',
    SAVE = 'save-_to_favourites',
    ADD = 'add_to_quote',
}

// eslint-disable-next-line @mpb-com/mpb/require-types-file -- Legacy code: do not use as an example.
export type ProductCardProps = {
    /**
     * The product we are displaying
     */
    product?: Product;

    /**
     * A list of buttons to be shown on the card, any combination of the following:
     * BASKET | SAVE | ADD
     */
    visibleButtons?: ProductCardButtons[];

    /**
     * Whether to display the product name or not
     */
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
    showProductName?: boolean;

    /**
     * Render components within the component as links to the product page
     */
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
    useInternalProductPageLinks?: boolean;

    /**
     * Selection ID for test purposes.
     */
    'data-testid'?: string;

    /**
     * Custom className
     */
    className?: string;

    /**
     * Handler for when the add button is clicked.
     */
    onAddClick?: (
        event: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>
    ) => void;

    /**
     * Whether to expand on hover
     */
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
    expandOnHover?: boolean;
};

type BufferedMeasurementsRefs = {
    highlightsContainer: MutableRefObject<HTMLDivElement | null>;
    boxListHeader: MutableRefObject<HTMLDivElement | null>;
    staticContainer: MutableRefObject<HTMLDivElement | null>;
};

// eslint-disable-next-line @mpb-com/mpb/require-constants-file, @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
export enum ProductCardBreakpointsEnum {
    SMALL = 'small',
    MEDIUM = 'medium',
    LARGE = 'large',
}

export const productCardBreakpoints = {
    [ProductCardBreakpointsEnum.SMALL]: 600,
    [ProductCardBreakpointsEnum.MEDIUM]: 800,
    [ProductCardBreakpointsEnum.LARGE]: 1000,
};

// eslint-disable-next-line @mpb-com/mpb/require-types-file -- Legacy code: do not use as an example.
export type ProductCardBreakpointsState = ElementBreakpointsStatus<
    typeof productCardBreakpoints
>;

// eslint-disable-next-line @mpb-com/mpb/require-types-file -- Legacy code: do not use as an example.
export type StyledProductCardProps = {
    productCardBreakpointsState: ProductCardBreakpointsState;
    measurements: BufferedMeasurements<BufferedMeasurementsRefs>;
    status: BufferedMeasurementsStatus;
    shouldShowProductBox: boolean;
    'data-testid'?: string;
};

const StyledProductCard = styled('div').withConfig({
    shouldForwardProp: (prop) => isPropValid(prop),
})`
    ${styles}
`;

export const ProductCard: FunctionComponent<ProductCardProps> = ({
    product,
    onAddClick,
    visibleButtons = [],
    showProductName = false,
    useInternalProductPageLinks = false,
    'data-testid': dataTestId = 'product-card',
    expandOnHover = true,
    ...otherProps
}: ProductCardProps) => {
    const productCardRef = useRef<HTMLDivElement | null>(null);
    const highlightsContainerRef = useRef<HTMLDivElement | null>(null);
    const boxListHeaderRef = useRef<HTMLDivElement | null>(null);
    const staticContainerRef = useRef<HTMLDivElement | null>(null);
    const { t } = useTranslation();
    const { isProductRecentlyViewed } = useRecentlyViewedProducts();

    const productCardBreakpointsState = useElementBreakpoints({
        ref: productCardRef,
        breakpoints: productCardBreakpoints,
        query: ElementBreakpointsQuery.WIDTH,
    });

    const bufferedMeasurementsRefs: BufferedMeasurementsRefs = {
        highlightsContainer: highlightsContainerRef,
        boxListHeader: boxListHeaderRef,
        staticContainer: staticContainerRef,
    };

    const { measurements, status, remeasure } = useBufferedMeasurements({
        refs: bufferedMeasurementsRefs,
    });

    const thumbnailAspectRatio = productCardBreakpointsState.small
        ? '4/3'
        : undefined;

    const thumbnailHref =
        (useInternalProductPageLinks && product?.linkHref) || '';

    const widthDependentProductBoxProps = getWidthDependentProductBoxProps(
        productCardBreakpointsState
    );

    const [isBoxListCollapsed, setIsBoxListCollapsed] = useState(
        widthDependentProductBoxProps.isCollapsable
    );

    const isLoading = Boolean(!product);

    const hasPriceModifiers = (product?.priceModifiers?.length ?? 0) > 0;

    const isFaultyCondition = isConditionSparesAndRepairs(product?.condition);

    const shouldShowProductBox = Boolean(
        isLoading || hasPriceModifiers || !isFaultyCondition
    );

    const shouldShowRecentlyViewedTag = Boolean(
        product?.id && isProductRecentlyViewed(product?.id)
    );

    const commonProps = {
        product,
        isLoading,
    };

    const renderFormattedCondition = () =>
        isLoading ? (
            <Skeleton variant={SkeletonVariant.TEXT} />
        ) : (
            product?.condition && (
                <FormattedCondition
                    condition={product.condition}
                    className="formatted-condition"
                />
            )
        );

    useEffect(() => {
        setIsBoxListCollapsed(true);
        if (!isLoading) {
            remeasure();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- Legacy code: do not use as an example.
    }, [productCardBreakpointsState, isLoading]);

    const handleToggleIsBoxListCollapsed = (
        event: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>
    ) => {
        if (widthDependentProductBoxProps.isCollapsable) {
            event?.preventDefault();
            event?.stopPropagation();
            setIsBoxListCollapsed(!isBoxListCollapsed);
        }
    };

    const renderHeading = () => {
        const shouldRenderHeading =
            showProductName && (isLoading || product?.name);

        if (!shouldRenderHeading) {
            return null;
        }

        const headingContent = isLoading ? (
            <Skeleton variant={SkeletonVariant.TEXT} />
        ) : (
            product?.name
        );

        return <Heading size={HeadingSize.H5}>{headingContent}</Heading>;
    };

    const renderProductBox = () => {
        if (isLoading) {
            return <ProductBoxSkeleton {...widthDependentProductBoxProps} />;
        }

        if (!shouldShowProductBox || !product) {
            return null;
        }

        return (
            <div
                className="product-box-container"
                data-testid="product-card__product-box-container"
                onClick={handleToggleIsBoxListCollapsed}
            >
                <ProductBox
                    {...widthDependentProductBoxProps}
                    condition={product?.condition}
                    priceModifiers={product?.priceModifiers ?? []}
                    isCollapsed={isBoxListCollapsed}
                />
            </div>
        );
    };

    const getVisibleButton = (button: ProductCardButtons, index: number) => {
        switch (button) {
            case ProductCardButtons.BASKET:
                return (
                    <AddToBasketButton
                        key={index}
                        data-testid="product-card__basket-button"
                        {...commonProps}
                    />
                );
            case ProductCardButtons.SAVE:
                return (
                    <FavouriteProductButton
                        key={index}
                        productId={product?.id}
                        isLoading={isLoading}
                    />
                );
            case ProductCardButtons.ADD:
                return (
                    <AddButton
                        key={index}
                        {...commonProps}
                        onAddClick={onAddClick}
                    />
                );
        }
    };

    return (
        <StyledProductCard
            productCardBreakpointsState={productCardBreakpointsState}
            measurements={measurements}
            status={status}
            shouldShowProductBox={shouldShowProductBox}
            data-testid={dataTestId}
            {...otherProps}
        >
            {/* eslint-disable-next-line react/jsx-no-leaked-render -- Legacy code: do not use as an example. */}
            {product?.tag && <UspTag tag={product?.tag} />}
            <CardBackground
                expandOnHover={expandOnHover}
                className="expanding-card-background"
                tag={
                    shouldShowRecentlyViewedTag
                        ? t('Recently viewed')
                        : undefined
                }
            >
                <div
                    className="results-container"
                    data-testid="product-card__results-container"
                    ref={productCardRef}
                >
                    <div className="image-container">
                        <Thumbnail
                            href={thumbnailHref}
                            target="_blank"
                            hasOutlineOnFocus
                            aspectRatio={thumbnailAspectRatio}
                            height={getThumbnailHeight()}
                            data-testid="product-card__thumbnail"
                        >
                            {isLoading || !product?.image ? (
                                <ThumbnailSkeleton
                                    data-testid="product-card__thumbnail-skeleton"
                                    className="thumbnail skeleton"
                                />
                            ) : (
                                <CloudflareImage
                                    data-testid="product-card__thumbnail-image"
                                    src={product.image}
                                    fallbackSrc={fallbackImagePath}
                                    alt={product?.name ?? ''}
                                    imageSize={CloudflareImageSize.THUMBNAIL}
                                />
                            )}
                        </Thumbnail>
                        <Sku {...commonProps} />
                    </div>
                    <div className="content-container">
                        <div
                            className="static-container"
                            ref={staticContainerRef}
                        >
                            <div className="details-container">
                                <div
                                    className="highlights-container"
                                    ref={highlightsContainerRef}
                                >
                                    {renderHeading()}
                                    <Price {...commonProps} />
                                    {renderFormattedCondition()}
                                    <Attributes
                                        {...commonProps}
                                        numExpectedAttributes={
                                            NUM_EXPECTED_ATTRIBUTES
                                        }
                                    />
                                </div>
                                {/* eslint-disable-next-line react/jsx-no-leaked-render -- Legacy code: do not use as an example. */}
                                {shouldShowProductBox && (
                                    <div
                                        className="inline-boxlist-container"
                                        onClick={handleToggleIsBoxListCollapsed}
                                    >
                                        <BoxListHeader
                                            ref={boxListHeaderRef}
                                            isLoading={isLoading}
                                            isToggleable={
                                                widthDependentProductBoxProps.isCollapsable
                                            }
                                            isToggled={isBoxListCollapsed}
                                            onToggle={
                                                handleToggleIsBoxListCollapsed
                                            }
                                        />
                                        {/* eslint-disable-next-line react/jsx-no-leaked-render -- Legacy code: do not use as an example. */}
                                        {productCardBreakpointsState.small &&
                                            renderProductBox()}
                                    </div>
                                )}
                            </div>
                            <div
                                className="buttons-container"
                                data-testid="product-card__buttons-container"
                            >
                                {visibleButtons.map(getVisibleButton)}
                            </div>
                        </div>
                        {/* eslint-disable-next-line react/jsx-no-leaked-render -- Legacy code: do not use as an example. */}
                        {!productCardBreakpointsState.small &&
                            shouldShowProductBox &&
                            renderProductBox()}
                    </div>
                </div>
            </CardBackground>
        </StyledProductCard>
    );
};
